/**
 * React Hook for RTDB-cached NHL standings with real-time updates
 *
 * TO BE IMPLEMENTED: Awaiting NHL API integration
 */

import { useState, useEffect, useCallback } from 'react';
import { rtdbNHLService } from '../rtdbNHLService';
import nhlRTDBCache from '../rtdbCache';
import { CACHE_PATHS, logCache } from '../rtdbConfig';

/**
 * Hook for fetching NHL standings with RTDB caching
 * @param {Object} params - Query parameters
 * @param {string} params.type - 'division' or 'conference'
 * @param {string} params.season - Season identifier (e.g., '2024-25')
 * @param {boolean} params.enabled - Whether to fetch data
 * @param {boolean} params.realtime - Enable real-time updates
 * @returns {Object} Standings data, loading state, error, and refetch function
 */
export const useRTDBStandings = ({
  type = 'division',
  season,
  enabled = true,
  realtime = true
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  const fetchStandings = useCallback(async () => {
    if (!enabled) return;

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');

      logCache('info', 'useRTDBStandings fetching', {
        type,
        season
      });

      // Convert season from '2025-26' format to '2025' (4-digit year) for API
      const apiSeason = season ? season.split('-')[0] : null;

      // NHL API doesn't have a 'type' parameter - standings are by league
      // For now, we'll use league=null to get all standings
      // TODO: Determine correct NHL league ID (e.g., 57 for NHL)
      const response = await rtdbNHLService.getStandings(null, apiSeason, null);

      const standings = response?.response || null;
      setData(standings);
      setCacheStatus('success');

      logCache('info', `useRTDBStandings loaded`);
    } catch (err) {
      setError(err.message || 'Failed to load standings');
      setCacheStatus('error');
      console.error('Failed to load NHL standings:', err);
    } finally {
      setLoading(false);
    }
  }, [type, season, enabled]);

  // Set up real-time subscription
  useEffect(() => {
    if (!enabled || !realtime) return;

    let unsubscribe = () => {};

    // Build cache path
    const cachePath = `${CACHE_PATHS.STANDINGS}/${type}_${season || 'current'}`;

    if (realtime) {
      // Subscribe to cache updates
      unsubscribe = nhlRTDBCache.subscribe(cachePath, (cachedStandings) => {
        if (cachedStandings) {
          setData(cachedStandings);
          setCacheStatus('realtime-update');
          logCache('info', 'useRTDBStandings realtime update');
        }
      });
    }

    return () => {
      unsubscribe();
    };
  }, [type, season, enabled, realtime]);

  // Initial fetch
  useEffect(() => {
    fetchStandings();
  }, [fetchStandings]);

  return {
    standings: data,
    loading,
    error,
    cacheStatus,
    refetch: fetchStandings
  };
};

export default useRTDBStandings;
